import React, { useEffect, useMemo, useState } from 'react';
import { Input, Spin } from 'antd';
import CurrencyInput from 'react-currency-input-field';
import designServices, { getRoofSquaresFromSystemDesign } from '../../../../services/oneButtonServices/designServices';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { Plus, Trash, FileText, Calendar, CurrencyDollar, Hash, ChartBar, Info } from '@phosphor-icons/react';

interface RoofAdder {
    id: number;
    item: string;
    description: string;
    quantity: number;
    unitPrice: number | string;
    subTotal: number;
    commissionable: boolean;
    discount?: number;
}

interface RoofAdderOption {
    label: string;
    value: string;
}
import type { selectedQuote } from './FinancingGridCard';
import { columnIncludesRoof, roofCalculatorC4FromColumn } from './quoteCreationProducts';
import { GenerateQuoteSectionHead } from './GenerateQuoteSectionHead';
import type { GqSectionStat } from './GenerateQuoteSectionHead.types';
import { OBDSelect } from './form-elements';
import './oneButton-css/RoofQuoteDetails.css';

function excelPartnerName(partner: string): string {
    switch (partner) {
        case 'GoodLeap':
        case 'Goodleep':
            return 'Good Leap';
        default:
            return partner;
    }
}

interface RoofQuoteDetailsProps {
    confirmQuoteCellKey: selectedQuote | null;
    priceQuote: any;
    theme: string;
    updateLoaderCount: (loading: boolean) => void;
    solarMasterSync?: {
        enabled: boolean;
        termApr?: string;
    };
}

const RoofQuoteDetails: React.FC<RoofQuoteDetailsProps> = ({ confirmQuoteCellKey, priceQuote, theme, updateLoaderCount, solarMasterSync }) => {
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const updateRoofDetails = useOneButtonStore((s) => s.updateRoofDetails);

    const pq = priceQuote || {};
    const installationState = pq.InstallationState ?? '';
    const roofBlock = pq.Roof || pq.RoofOnly || {};
    const manufacturer = roofBlock.ManufacturerName ?? '';
    const roofProduct = roofBlock.RoofProduct ?? '';
    const roofingTeam = pq.Roofing_Team ?? 'Solar';
    const sddf = pq.SDDF_Lead_source ?? '';
    const salesDiv = pq.Sales_Division ?? '';
    // Initial state setup from API response
    const rq = pq.RoofQuote || {};
    const lowPriceOverride = rq.Low_Slope_Price_Override != null ? Number(rq.Low_Slope_Price_Override) : 0;
    const normalPriceOverride = rq.Normal_Slope_Price_Override != null ? Number(rq.Normal_Slope_Price_Override) : (pq.PricePerSqFeet || 0);

    const RoofDiscountCount = {
        'Plywood': 5,
        'Skylight_Curb_Kits': 4
    }
    const [roofDesignDetails, setRoofDesignDetails] = useState<any>(null);

    useEffect(() => {
        const sfId = pq?.SalesForce_Id || opportunityDetails?.SalesForce_Id;
        if (!sfId) return;

        const loadRoofDesign = async () => {
            setLoadingMap(prev => ({ ...prev, methods: true }));
            try {
                const source = pq?.Design_Source?.toLowerCase();
                let res;
                if (source === 'onedraw') {
                    if (sfId) res = await designServices.RetrieveBuildingWithRoof(sfId);
                } else {
                    if (sfId) res = await designServices.GetRoofAreasByOpportunityId(sfId);
                }
                setRoofDesignDetails(res || {});
            } catch (err) {
                console.error('Failed to load roof design details', err);
            } finally {
                setLoadingMap(prev => ({ ...prev, methods: false }));
            }
        };
        loadRoofDesign();
    }, [opportunityDetails?.SalesForce_Id, pq?.Design_Source]);

    const roundedBasedOn33Percent = (val: number) => {
        const decimalPart = Number(val - Math.floor(val)).toFixed(2);
        if (Number(decimalPart) >= 0.33) {
            return Math.ceil(val);
        } else {
            return Math.floor(val);
        }
    };

    const calculateRoofSquares = (designDetails: any) => {
        let total = 0;
        let lowRoofsTotal = 0;
        let normalRoofsTotal = 0;

        if (designDetails?.Building?.length > 0) {
            designDetails.Building.forEach((b: any) => {
                let lowRoofs = 0;
                let normalRoofs = 0;
                if (Array.isArray(b.Roof) && b.Roof.length > 0) {
                    b.Roof.forEach((r: any) => {
                        // Using Active_Ind !== false to support both true and undefined
                        if (r.Active_Ind !== false) {
                            lowRoofs += (Number(r.Low_Square ?? r.LowSquare) || 0);
                            normalRoofs += (Number(r.Normal_Square ?? r.NormalSquare) || 0);
                        }
                    });
                    lowRoofsTotal += roundedBasedOn33Percent(lowRoofs);
                    normalRoofsTotal += roundedBasedOn33Percent(normalRoofs);
                }
            });
        }

        let low_slope_squares = (pq?.Low_Slope_Override !== null && pq?.Low_Slope_Override !== undefined) ? Number(pq?.Low_Slope_Override) : lowRoofsTotal;
        let normal_slope_squares = (pq?.Normal_Slope_Override !== null && pq?.Normal_Slope_Override !== undefined) ? Number(pq?.Normal_Slope_Override) : normalRoofsTotal;
        total = low_slope_squares + normal_slope_squares;
        
        return { 
            low_slope_squares, 
            normal_slope_squares, 
            total_roof_squares: total 
        };
    };

    const roofMetrics = useMemo(() => calculateRoofSquares(roofDesignDetails), [roofDesignDetails, pq]);

    const [financeMethods, setFinanceMethods] = useState<{ value: string; label: string }[]>([]);
    const [termOptions, setTermOptions] = useState<{ value: string; label: string }[]>([]);
    // Initial binding for Term & APR
    const selectedQuote = (pq.QuoteVersion || []).find((qv: any) => qv.Selected);
    const initialTermAprValue = rq.TermAPR || selectedQuote?.TermAPR || '';
    
    const [termApr, setTermApr] = useState(String(initialTermAprValue));
    const [deposit, setDeposit] = useState<number>(Number(rq.Deposit_Amount || 0));
    const [pricingOverrideType, setPricingOverrideType] = useState(rq.Pricing_Override_Type || 'RoofSquares');
    const [normalSlopePriceOverride, setNormalSlopePriceOverride] = useState<number | string>(normalPriceOverride);
    const [lowSlopePriceOverride, setLowSlopePriceOverride] = useState<number | string>(lowPriceOverride || (normalPriceOverride ? Number(normalPriceOverride) + 0.7 : 0));
    const [selectedFinanceMethod, setSelectedFinanceMethod] = useState('');
    const [pricingStats, setPricingStats] = useState<any>(null);
    const [overrideStats, setOverrideStats] = useState<any>(null);
    const [loadingMap, setLoadingMap] = useState({ methods: false, terms: false, stats: false, overrides: false, summary: false, adders: false });
    const isComponentLoading = Object.values(loadingMap).some(v => v);

    useEffect(() => {
        if (isComponentLoading) {
            updateLoaderCount(true);
            return () => updateLoaderCount(false);
        }
    }, [isComponentLoading, updateLoaderCount]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const partner = confirmQuoteCellKey?.partner || '';
    const productCol = confirmQuoteCellKey?.product || '';

    const c4 = useMemo(() => {
        return roofCalculatorC4FromColumn(productCol, partner);
    }, [productCol, partner]);

    const excelP = excelPartnerName(partner);


    const [adderOptions, setAdderOptions] = useState<RoofAdderOption[]>([]);
    const [selectedAdderType, setSelectedAdderType] = useState<string>('');
    const [offerId, setOfferId] = useState<string>('');
    const [existingOfferIdDetails, setExistingOfferIdDetails] = useState<any[]>([]);
    
    useEffect(() => {
        if ((partner === 'GoodLeap' || partner === 'Good Leap') && opportunityDetails?.Opportunity_Id) {
            quoteServices.ChangeOrderOffer(opportunityDetails.Opportunity_Id).then(res => {
                if (Array.isArray(res)) {
                    setExistingOfferIdDetails(res);
                }
            }).catch(e => console.error(e));
        }
    }, [partner, opportunityDetails?.Opportunity_Id]);

    const [activeAdders, setActiveAdders] = useState<RoofAdder[]>(() => {
        if (!pq.RoofAdders) return [];
        return pq.RoofAdders.map((a: any, i: number) => ({
            id: i, item: a.AdderSelect, description: a.Description, quantity: a.AdderQuantity, unitPrice: a.UnitPrice, subTotal: a.AdderCost, commissionable: a.Commissonable === 'Y'
        }));
    });
    const [rrPrice, setRrPrice] = useState<number>(0);
    const [rrUnitPrice, setRrUnitPrice] = useState<number | string>(0);
    const [rrPriceLoading, setRrPriceLoading] = useState(false);
    const [totalCommissionAmount, setTotalCommissionAmount] = useState<number>(0);
    const [totalSales, setTotalSales] = useState<number>(0);
    const [totalRoofAmount, setTotalRoofAmount] = useState<number>(Number(pq.Total_Roof_Cost || 0));
    const [roofCostType, setRoofCostType] = useState(rq.Roof_Cost_Type || 'PricingOverride');
    const [priceTiers, setPriceTiers] = useState(rq.Pricing_Tier || '');
    const [totalRoofingOverride, setTotalRoofingOverride] = useState<number | string>(Number(rq.Total_Roofing_Override || 0));
    const [summaryStats, setSummaryStats] = useState({
        totalAdderCost: Number(pq.TotalRoof || 0),
        totalRoofCost: 0,
        totalAdderComm: 0,
        roofComm: 0,
        financedAmount: 0,
        monthlyPayment: 0,
        dealerFee: 0,
        dealerEpc: 0,
        purchaseMethod: '',
        plusAddersUponCompletion: Number(rq.PlusAddersUponCompletionOfWork || 0)
    });

    const isCashPartner = partner === 'Cash';
    const selectDropdownClassName = `obd-select-dropdown obd-roof-quote-select-dropdown obd-theme-${theme}`;

    useEffect(() => {
        if (!solarMasterSync?.enabled) return;
        if (isCashPartner || !selectedFinanceMethod?.toLowerCase().includes('loan')) return;
        const syncedTermApr = String(solarMasterSync.termApr ?? '').trim();
        if (!syncedTermApr) return;
        if (!termOptions.some((o) => String(o.value).trim() === syncedTermApr)) return;
        if (termApr === syncedTermApr) return;
        setTermApr(syncedTermApr);
    }, [solarMasterSync, isCashPartner, selectedFinanceMethod, termOptions, termApr]);

    const structuralInputs = useMemo(() => {
        if (!confirmQuoteCellKey) return [];
        return [
            { Type: 'string', CellNumber: 'C3', CellValue: installationState },
            { Type: 'string', CellNumber: 'C4', CellValue: c4 },
            { Type: 'string', CellNumber: 'C5', CellValue: roofingTeam },
            { Type: 'string', CellNumber: 'C6', CellValue: manufacturer },
            { Type: 'string', CellNumber: 'C7', CellValue: roofProduct },
            { Type: 'string', CellNumber: 'C8', CellValue: String(roofMetrics.low_slope_squares) },
            { Type: 'string', CellNumber: 'C9', CellValue: String(roofMetrics.normal_slope_squares) },
            { Type: 'string', CellNumber: 'C12', CellValue: roofMetrics.total_roof_squares },
            { Type: 'string', CellNumber: 'R3', CellValue: excelP },
        ];
    }, [confirmQuoteCellKey, installationState, c4, roofingTeam, manufacturer, roofProduct, roofMetrics, excelP]);

    // Explicitly reset the cascading state when moving between cells
    useEffect(() => {
        if (!confirmQuoteCellKey) return;
        setFinanceMethods([]);
        setSelectedFinanceMethod('');
        setTermOptions([]);
        setTermApr('');
        setPricingStats(null);
        setOverrideStats(null);
        setDeposit(0);
    }, [confirmQuoteCellKey?.partner, confirmQuoteCellKey?.product, confirmQuoteCellKey?.methord, rq.id]);



    const [committedPricing, setCommittedPricing] = useState({ 
        deposit, 
        normalPrice: normalSlopePriceOverride, 
        lowPrice: lowSlopePriceOverride, 
        totalOverride: totalRoofingOverride 
    });

    // Ensure committedPricing is synced on initial load with the current state values
    useEffect(() => {
        setCommittedPricing({ 
            deposit, 
            normalPrice: normalSlopePriceOverride, 
            lowPrice: lowSlopePriceOverride, 
            totalOverride: totalRoofingOverride 
        });
    }, [rq.id]); // Re-sync if the quote fundamentally changes

    const handlePricingBlur = () => {
        setIsInputFocused(false);
        setCommittedPricing({ 
            deposit: Number(deposit) || 0, 
            normalPrice: Number(normalSlopePriceOverride) || 0, 
            lowPrice: Number(lowSlopePriceOverride) || 0, 
            totalOverride: Number(totalRoofingOverride) || 0 
        });
    };

    const baseInputs = useMemo(() => {
        if (!confirmQuoteCellKey) return [];
        return [
            ...structuralInputs,
            { Type: 'string', CellNumber: 'H5', CellValue: committedPricing.deposit || 0 },
            { Type: 'string', CellNumber: 'H6', CellValue: sddf || "" },
            { Type: 'string', CellNumber: 'H7', CellValue: salesDiv || "" },
            { Type: 'string', CellNumber: 'H10', CellValue: String(committedPricing.normalPrice || 0) },
            { Type: 'string', CellNumber: 'H9', CellValue: String(committedPricing.lowPrice || 0) }
        ];
    }, [structuralInputs, confirmQuoteCellKey, committedPricing.deposit, committedPricing.normalPrice, committedPricing.lowPrice, sddf, salesDiv]);

    // 1. Fetch T4 (Finance Methods) - Narrow dependencies & prevent redundant load triggers
    useEffect(() => {
        if (!confirmQuoteCellKey || !columnIncludesRoof(confirmQuoteCellKey.product)) return;
        if (!installationState || !excelP || (pq?.Design_Source && !roofDesignDetails)) return;
        
        setLoadingMap(prev => ({ ...prev, methods: true }));
        masterServices.cascadingDropDown({ ExcelFileName: 'Roofing Calculator', SheetName: 'Roof Pricing', InputValues: structuralInputs, OutputCells: ['T4'] })
            .then((res: any) => {
                const output = Array.isArray(res) ? res : res?.OutputValues || [];
                const val = output.find((o: any) => o.CellNumber === 'T4')?.CellValue;
                if (!val) { setFinanceMethods([]); return; }
                const methods = String(val).split('|').map(s => s.trim()).filter(Boolean).map(m => ({ value: m, label: m }));
                const isGoodLeap = partner === 'GoodLeap' || partner === 'Goodleep';
                const filtered = methods.filter(m => isGoodLeap ? true : m.label !== 'Loan');
                setFinanceMethods(filtered);
                
                const gridMethod = (confirmQuoteCellKey.methord || '').trim();
                const matched = (gridMethod && filtered.some(x => x.value === gridMethod)) ? gridMethod : filtered[0]?.value || '';
                if (!selectedFinanceMethod || !filtered.some(f => f.value === selectedFinanceMethod)) { setSelectedFinanceMethod(matched); }

                // Case-insensitive check for 'Cash Contract'
                if (isCashPartner && matched.toLowerCase() === 'cash contract') {
                    setTermApr('N/A');
                }
            })
            .catch(() => setLoadError('Roof finance methods fetch failed.'))
            .finally(() => setLoadingMap(prev => ({ ...prev, methods: false })));
    }, [structuralInputs, partner, confirmQuoteCellKey?.methord, installationState, excelP, roofDesignDetails]);

    // 2. Fetch T5 (Term & APR options)
    useEffect(() => {
        if (!confirmQuoteCellKey || !selectedFinanceMethod || !installationState) return;
        if (isCashPartner && selectedFinanceMethod.toLowerCase() === 'cash contract') {
            setTermOptions([{ value: 'N/A', label: 'N/A' }]);
            setTermApr('N/A');
            return;
        }

        setLoadingMap(prev => ({ ...prev, terms: true }));

        const payload = {
            ExcelFileName: 'Roofing Calculator', SheetName: 'Roof Pricing',
            InputValues: [
                ...structuralInputs,
                { Type: 'string', CellNumber: 'R4', CellValue: "" },
                { Type: 'string', CellNumber: 'R5', CellValue: selectedFinanceMethod.trim() },
                { Type: 'string', CellNumber: 'H5', CellValue: committedPricing.deposit || 0 },
                { Type: 'string', CellNumber: 'H6', CellValue: sddf || "" },
                { Type: 'string', CellNumber: 'H7', CellValue: salesDiv || "" },
            ],
            OutputCells: ['T5']
        };

        masterServices.cascadingDropDown(payload).then((res: any) => {
            const output = Array.isArray(res) ? res : res?.OutputValues || [];
            const termsVal = output.find((o: any) => o.CellNumber === 'T5')?.CellValue;
            if (termsVal) {
                const terms = String(termsVal).split('|').map(s => s.trim()).filter(Boolean).map(t => ({ value: t, label: t }));
                setTermOptions(terms);
                
                // Formulate target term/apr strings to see if we have a match in the dropdown
                const cellTerm = Number(confirmQuoteCellKey?.term);
                const cellApr = Number(confirmQuoteCellKey?.apr);
                let selectedMatchedValue = '';

                if (!isNaN(cellTerm) && !isNaN(cellApr)) {
                    const target = `${cellTerm} years at ${cellApr}%`.toLowerCase();
                    const found = terms.find(t => {
                        const v = t.value.toLowerCase();
                        if (v === target) return true;
                        const nums = v.match(/\d+(\.\d+)?/g)?.map(Number);
                        return nums?.includes(cellTerm) && nums?.includes(cellApr);
                    });
                    if (found) selectedMatchedValue = found.value;
                }

                setTermApr(prev => {
                    const currentValid = terms.some(t => t.value === prev);
                    const isNewOrInvalid = !prev || prev === '' || prev === 'Select' || !currentValid;
                    
                    if (isNewOrInvalid) {
                        return selectedMatchedValue || (terms.length > 0 ? terms[0].value : '');
                    }
                    return prev;
                });
            } else {
                setTermOptions([]);
            }
        }).finally(() => {
            setLoadingMap(prev => ({ ...prev, terms: false }));
        });
    }, [structuralInputs, selectedFinanceMethod, committedPricing.deposit, sddf, salesDiv, confirmQuoteCellKey?.term, confirmQuoteCellKey?.apr, initialTermAprValue]);

    // 3. Combined Pricing & Stats (Previously Step 3 & 3.5)
    useEffect(() => {
        if (!confirmQuoteCellKey || !selectedFinanceMethod || !termApr || termApr === 'Select') return;
        setLoadingMap(prev => ({ ...prev, stats: true, overrides: true }));

        const fetchPricingData = async () => {
            const isFixed = pricingOverrideType === 'Fixed';
            const outputCells = isFixed 
                ? ["E6", "E7", "J7", "J8", "J42", "J43"] // Overrides set
                : ["E6", "E7", "J4", "J5", "J6", "J7", "J42", "J43"]; // Stats set

            const payload = {
                ExcelFileName: 'Roofing Calculator', SheetName: 'Roof Pricing',
                InputValues: [
                    ...baseInputs,
                    { Type: 'string', CellNumber: "H3", CellValue: priceTiers || "" },
                    { Type: 'string', CellNumber: "M3", CellValue: String(summaryStats.totalAdderCost) },
                    { Type: 'string', CellNumber: "R3", CellValue: excelP || "" },
                    { Type: 'string', CellNumber: "R5", CellValue: selectedFinanceMethod?.trim() || "" },
                    { Type: 'string', CellNumber: "R6", CellValue: termApr.trim() },
                    { Type: 'string', CellNumber: "H4", CellValue: String(committedPricing.totalOverride || 0) },
                    { Type: 'string', CellNumber: "H8", CellValue: String(summaryStats.plusAddersUponCompletion || 0) }
                ],
                OutputCells: outputCells
            };

            try {
                const res = await masterServices.cascadingDropDown(payload);
                const output = Array.isArray(res) ? res : (res as any)?.OutputValues || [];
                const statsObj: any = {};
                output.forEach((o: any) => { statsObj[o.CellNumber] = o.CellValue; });

                if (isFixed) {
                    setOverrideStats(statsObj);
                    const isCashLoan = (selectedFinanceMethod === 'Cash Contract' || selectedFinanceMethod?.toLowerCase().includes('loan'));
                    let floorRate = statsObj['J42'];
                    if (isCashLoan) floorRate = statsObj['J43'];

                    if (Number(committedPricing.totalOverride) > 0) {
                        const newNormal = floorRate ? Number(Number(floorRate).toFixed(2)) : '';
                        const newLow = floorRate ? Number((Number(floorRate) + 0.7).toFixed(2)) : '';
                        
                        setNormalSlopePriceOverride(prev => prev !== newNormal ? newNormal : prev);
                        setLowSlopePriceOverride(prev => prev !== newLow ? newLow : prev);
                        
                        // We also need to update committedPricing so these new values are used in subsequent Excel calls
                        setCommittedPricing(prev => {
                            if (prev.normalPrice === Number(newNormal) && prev.lowPrice === Number(newLow)) return prev;
                            return {
                                ...prev,
                                normalPrice: Number(newNormal) || 0,
                                lowPrice: Number(newLow) || 0
                            };
                        });
                    }
                } else {
                    setPricingStats(statsObj);
                }
            } catch (err) {
                console.error('Pricing data fetch failed', err);
                setLoadError('Failed to fetch pricing calculations.');
            } finally {
                setLoadingMap(prev => ({ ...prev, stats: false, overrides: false }));
            }
        };

        fetchPricingData();
    }, [baseInputs, selectedFinanceMethod, termApr, pricingOverrideType, priceTiers, committedPricing.totalOverride]);

    // 4a. Fetch GoodLeap Offer ID (Another T4 Call) - Narrowly triggered
    useEffect(() => {
        const isGoodLeap = excelP === 'Good Leap' || excelP === 'GoodLeap' || partner === 'GoodLeap';
        if (!isGoodLeap || !selectedFinanceMethod || !termApr || termApr === 'Select') {
            setOfferId('');
            return;
        }

        const fetchGoodLeapOfferId = async () => {
            const utilityCompanyName = systemDesign?.SolarUtility?.UtilityCompanyName || '';
            const financialData = {
                ExcelFileName: "Sunnova Financial Data",
                SheetName: "SVA Financing Options",
                InputValues: [
                    { Type: 'string', CellNumber: "B4", CellValue: installationState },
                    { Type: 'string', CellNumber: "C4", CellValue: c4 === 'SBR' || c4 === 'SR' || partner === 'GoodLeap' ? 'R' : c4 },
                    { Type: 'string', CellNumber: "D4", CellValue: "Good Leap" },
                    { Type: 'string', CellNumber: "F4", CellValue: selectedFinanceMethod.trim() },
                    { Type: 'string', CellNumber: "K4", CellValue: 0 },
                    { Type: 'string', CellNumber: "O4", CellValue: termApr.trim() },
                    { Type: 'string', CellNumber: "E8", CellValue: utilityCompanyName || '' },
                ],
                OutputCells: ["T4", "U11"],
            };
            let localOfferId = '';
            let fallbackUsed = false;
            try {
                if (existingOfferIdDetails.length > 0) {
                    const isLoan = selectedFinanceMethod?.toLowerCase().includes('loan');
                    const escalatorVal = 0;
                    const match = existingOfferIdDetails.find((o: any) => {
                        if (o.FinanceMethod !== selectedFinanceMethod) return false;
                        if (isLoan) {
                            return o.TermAPR?.trim() === termApr.trim();
                        } else {
                            return o.Escalator === escalatorVal && !o.isUtilityUpdated;
                        }
                    });
                    
                    if (match?.OfferId) {
                        localOfferId = match.OfferId;
                        fallbackUsed = true;
                    }
                }

                if (!fallbackUsed) {
                    updateLoaderCount(true);
                    const offerRes = await masterServices.cascadingDropDown(financialData);
                    const offerOutput = Array.isArray(offerRes) ? offerRes : (offerRes as any)?.OutputValues || [];
                    const foundOffer = offerOutput.find((o: any) => o.CellNumber === 'T4')?.CellValue;
                    if (foundOffer) localOfferId = String(foundOffer);
                }
                setOfferId(localOfferId);
            } catch (e) {
                console.error('Failed to get good leap offer id', e);
            } finally {
                if (!fallbackUsed) updateLoaderCount(false);
            }
        };

        fetchGoodLeapOfferId();
    }, [installationState, c4, excelP, partner, selectedFinanceMethod, termApr, existingOfferIdDetails, systemDesign?.SolarUtility?.UtilityCompanyName]);

    // 4b. Final Summary (W3-W8) 
    useEffect(() => {
        if (!confirmQuoteCellKey || !selectedFinanceMethod || !termApr || termApr === 'Select' || (!overrideStats && !pricingStats)) return;
        setLoadingMap(prev => ({ ...prev, summary: true }));
        
        const fetchFinalSummary = async () => {
            const payload = {
                ExcelFileName: 'Roofing Calculator', SheetName: 'Roof Pricing',
                InputValues: [
                    ...baseInputs,
                    { Type: 'string', CellNumber: "H3", CellValue: priceTiers || "" },
                    { Type: 'string', CellNumber: "M3", CellValue: String(summaryStats.totalAdderCost) },
                    { Type: 'string', CellNumber: "R4", CellValue: "" },
                    { Type: 'string', CellNumber: "R5", CellValue: selectedFinanceMethod.trim() },
                    { Type: 'string', CellNumber: "R6", CellValue: termApr.trim() },
                    { Type: 'string', CellNumber: "H4", CellValue: String(committedPricing.totalOverride || 0) },
                    { Type: 'string', CellNumber: "H8", CellValue: String(summaryStats.plusAddersUponCompletion || 0) }
                ],
                OutputCells: ["J8", "W3", "W4", "W5", "W6", "W7", "T6", "T7", "T8", "W8"]
            };

            try {
                const res = await masterServices.cascadingDropDown(payload);
                const output = Array.isArray(res) ? res : (res as any)?.OutputValues || [];
                const getValue = (cell: string) => output.find((o: any) => o.CellNumber === cell)?.CellValue;

                const parseNumericCell = (cellValue: any) => {
                    if (!cellValue) return 0;
                    if (typeof cellValue === 'number') return cellValue;
                    const clean = String(cellValue).replace(/[$,]/g, '').trim();
                    const num = Number(clean);
                    return isNaN(num) ? 0 : num;
                };

                const roofTotal = parseNumericCell(getValue('J8'));
                const salesTotal = parseNumericCell(getValue('W3'));
                const commTotal = parseNumericCell(getValue('W4'));
                const financedVal = parseNumericCell(getValue('W8'));
                const monthlyPayment = parseNumericCell(getValue('W5'));
                const dealerFee = parseNumericCell(getValue('W6'));
                const dealerEpc = parseNumericCell(getValue('W7'));
                const pMethod = getValue('T8');
                
                setTotalRoofAmount(roofTotal);
                setTotalSales(salesTotal);
                setTotalCommissionAmount(commTotal);

                setSummaryStats(prev => ({
                    ...prev,
                    totalRoofCost: roofTotal,
                    financedAmount: financedVal,
                    roofComm: commTotal,
                    monthlyPayment,
                    dealerFee,
                    dealerEpc,
                    purchaseMethod: pMethod
                }));
            } catch (err) {
                console.error('Roof calculation failed', err);
                setLoadError('Roof calculation failed.');
            } finally {
                setLoadingMap(prev => ({ ...prev, summary: false }));
            }
        };

        fetchFinalSummary();
    }, [overrideStats, pricingStats, summaryStats.totalAdderCost]);

    // Sync all roof state into the Zustand store so GenerateQuoteModal can read it
    useEffect(() => {
        const isRRR = opportunityDetails?.OpportunityInterest?.RRR;
        const [term, apr] = termApr ? termApr.replace("APR", "").split(" years at ") : ["0", "0"];
        
        const data = {
            financePartnerName: excelP,
            contract_Type: selectedFinanceMethod,
            term: Number(String(term).match(/\d+/)?.[0] || 0),
            apr: Number(String(apr || term).replace(/[^\d.]/g, '') || 0),
            TermAPR: termApr,
            CustomerDeposit: deposit,
            gross_Sales_Price: totalSales,
            deposit: deposit,
            sales_Commission: Number(totalCommissionAmount),
            Roofsq: roofMetrics.total_roof_squares,
            RoofCostType: roofCostType,
            commission_Tier: priceTiers,
            purchaseMethod: summaryStats.purchaseMethod,
            PlusAddersUponCompletionOfWork: summaryStats.plusAddersUponCompletion,
            Deposit_Amount: deposit,
            Financed_Amount: summaryStats.financedAmount,
            Total_Roof_Cost: totalRoofAmount,
            Dealer_Fee_Percentage: String((summaryStats as any).dealerFee),
            Dealer_Epc: String((summaryStats as any).dealerEpc),
            Monthly_Finance_Partner_Payment: (summaryStats as any).monthlyPayment,
            RoofAdders: [
                ...(isRRR ? [{
                    AdderSelect: 'R&R Price',
                    Description: '',
                    AdderQuantity: roofMetrics.total_roof_squares,
                    UnitPrice: 0,
                    AdderCost: rrPrice || 0,
                    Commissonable: 'N',
                    RR_Price: rrPrice
                }] : []),
                ...activeAdders.map(a => ({
                    AdderSelect: a.item,
                    Description: a.description,
                    AdderQuantity: a.quantity,
                    UnitPrice: a.unitPrice,
                    AdderCost: a.subTotal,
                    Commissonable: a.commissionable ? 'Y' : 'N'
                }))
            ],
            roofCalculatorC4: c4,
            normalSlopePriceOverride,
            lowSlopePriceOverride,
            Pricing_Override_Type: pricingOverrideType,
            Total_Roofing_Override: totalRoofingOverride,
            RR_Price: rrPrice,
            RR_UnitPrice: rrUnitPrice,
            Offer_Id: offerId,
            financePartnerProductId: offerId,
            valid: true
        };
        updateRoofDetails(data);
    }, [partner, selectedFinanceMethod, termApr, deposit, totalSales, totalCommissionAmount,
        roofMetrics.total_roof_squares, roofCostType, priceTiers, summaryStats,
        totalRoofAmount, activeAdders, c4, normalSlopePriceOverride, lowSlopePriceOverride,
        pricingOverrideType, totalRoofingOverride, rrPrice, rrUnitPrice, offerId,
        opportunityDetails?.OpportunityInterest?.RRR]);

    const handleNormalPriceChange = (val: number | string) => {
        setNormalSlopePriceOverride(val);
        const numVal = Number(val) || 0;
        if (numVal > 0) {
            setLowSlopePriceOverride(Number((numVal + 0.7).toFixed(2)));
        } else {
            setLowSlopePriceOverride(0);
        }
    };

    useEffect(() => {
        if (!confirmQuoteCellKey) return;
        updateLoaderCount(true);
        masterServices.cascadingDropDown({ ExcelFileName: "Roofing Calculator", SheetName: "Adder Pricing", InputValues: [], OutputCells: ["C4"] }).then(res => {
            const outputValues = Array.isArray(res) ? res : res?.OutputValues;
            if (outputValues?.[0]?.CellValue) {
                const options = String(outputValues[0].CellValue).split('|').map(ele => ele.trim()).filter(Boolean).map(ele => ({ label: ele, value: ele }));
                setAdderOptions(options);
                if (options.length > 0 && !selectedAdderType) { setSelectedAdderType(options[0].value); }
            }
        }).finally(() => {
            updateLoaderCount(false);
        });
        if (opportunityDetails?.OpportunityInterest?.RRR && opportunityDetails?.RR_System_Size != null) { 
            getRRPrice(false, true); 
        }
    }, [confirmQuoteCellKey, opportunityDetails?.RR_System_Size, sddf, salesDiv]);

    const getValue = (cell: string, arr: any[]): any => {
        const result = arr.find(o => o.CellNumber === cell);
        return result?.CellValue;
    };

    const getRRPrice = async (updateUnitPrice: boolean, status?: boolean) => {
        setRrPriceLoading(true);
        updateLoaderCount(true);
        let payload: any;
        
        if (updateUnitPrice) {
            payload = {
                ExcelFileName: "Roofing Calculator", 
                SheetName: "Adder Pricing",
                InputValues: [
                    { Type: 'string', CellNumber: "F4", CellValue: 'RRR' }, 
                    { Type: 'string', CellNumber: "G4", CellValue: opportunityDetails?.RR_System_Size }, 
                    { Type: 'string', CellNumber: "H4", CellValue: rrUnitPrice },
                    { Type: 'string', CellNumber: "H6", CellValue: sddf || "" }, 
                    { Type: 'string', CellNumber: "H7", CellValue: salesDiv || "" }
                ],
                OutputCells: ["J4", "C7", "I4"]
            };
        } else {
            payload = {
                ExcelFileName: "Roofing Calculator", 
                SheetName: "Adder Pricing",
                InputValues: [
                    { Type: 'string', CellNumber: "F4", CellValue: 'RRR' }, 
                    { Type: 'string', CellNumber: "G4", CellValue: opportunityDetails?.RR_System_Size }, 
                    { Type: 'string', CellNumber: "H6", CellValue: sddf || "" }, 
                    { Type: 'string', CellNumber: "H7", CellValue: salesDiv || "" }
                ],
                OutputCells: ["J4", "C7", "I4"]
            };
        }

        try {
            const res = await masterServices.cascadingDropDown(payload);
            const output = Array.isArray(res) ? res : (res as any)?.OutputValues || [];
            
            const price = Number(getValue('J4', output) || 0);
            const unitP = Number(getValue('I4', output) || 0);
            
            setRrPrice(price);
            if (!updateUnitPrice) {
                setRrUnitPrice(unitP);
            }
            
            if (status) {
                await getRRPrice(true, false);
            } else {
                await calculateGlobalAdders(activeAdders);
            }
        } catch (err) {
            console.error('RR Price fetch failed', err);
        } finally {
            setRrPriceLoading(false);
            updateLoaderCount(false);
        }
    };

    const calculateGlobalAdders = async (adders: RoofAdder[]) => {
        updateLoaderCount(true);
        let payload: any = [];
        const isRRR = opportunityDetails?.OpportunityInterest?.RRR;
        
        if (isRRR) {
            payload.push( 
                { Type: 'string', CellNumber: 'F4', CellValue: 'RRR' }, 
                { Type: 'string', CellNumber: 'G4', CellValue: opportunityDetails?.RR_System_Size || 0 },
                { Type: 'string', CellNumber: 'H4', CellValue: rrUnitPrice || 0 }
            );
        } else {
             payload.push( 
                { Type: 'string', CellNumber: 'F4', CellValue: 'RRR' }, 
                { Type: 'string', CellNumber: 'G4', CellValue: opportunityDetails?.RR_System_Size || 0 }
            );
        }

        adders.forEach((ele, idx) => {
            const rowNumber = Number(idx + 4);
            payload.push( 
                { Type: 'string', CellNumber: `F${rowNumber}`, CellValue: ele.item }, 
                { Type: 'string', CellNumber: `G${rowNumber}`, CellValue: ele.quantity || 0 } 
            );
        });

        payload.push(
            { Type: 'string', CellNumber: "H6", CellValue: sddf || "" }, 
            { Type: 'string', CellNumber: "H7", CellValue: salesDiv || "" }
        );

        try {
            const res = await masterServices.cascadingDropDown({
                ExcelFileName: "Roofing Calculator", 
                SheetName: "Adder Pricing",
                InputValues: payload,
                OutputCells: ["J24", "C7"]
            });

            const output = Array.isArray(res) ? res : (res as any)?.OutputValues || [];
            const totalComm = Number(getValue('J24', output) || 0);
            const totalCostValue = Number(getValue('C7', output) || 0);

            setTotalCommissionAmount(totalComm);
            setSummaryStats(prev => ({
                ...prev,
                totalAdderCost: totalCostValue,
                totalAdderComm: totalComm,
                totalRoofCost: (prev.totalRoofCost || 0) // Maintain roof cost
            }));
        } catch (err) {
            console.error('Failed to calculate global adders', err);
        } finally {
            updateLoaderCount(false);
        }
    };

    const handleAddAdder = async () => {
        if (!selectedAdderType) return;
        updateLoaderCount(true);
        
        const nextRow = activeAdders.length + 4;
        const payload = {
            ExcelFileName: "Roofing Calculator", 
            SheetName: "Adder Pricing",
            InputValues: [
                { Type: 'string', CellNumber: `F${nextRow}`, CellValue: selectedAdderType },
                { Type: 'string', CellNumber: "H6", CellValue: sddf || "" }, 
                { Type: 'string', CellNumber: "H7", CellValue: salesDiv || "" }
            ],
            OutputCells: [`I${nextRow}`, `K${nextRow}`]
        };

        const typeBuffer = selectedAdderType;
        setSelectedAdderType('');

        try {
            await masterServices.cascadingDropDown(payload);
            const newAdder: RoofAdder = {
                id: Date.now(), // Generate a unique ID for React keys
                item: typeBuffer,
                description: '',
                quantity: 0,
                unitPrice: 0,
                subTotal: 0,
                commissionable: false
            };
            
            const nextAdders = [...activeAdders, newAdder];
            setActiveAdders(nextAdders);
            
            if (adderOptions.length > 0) {
                setSelectedAdderType(adderOptions[0].value);
            }
        } catch (err) {
            console.error('Failed to add adder', err);
            setSelectedAdderType(typeBuffer);
        } finally {
            updateLoaderCount(false);
        }
    };

    const updateAdderQuantity = async (index: number, qty: number, reset?: boolean) => {
        setIsInputFocused(false);
        let currentQty = qty;
        if (reset) currentQty = 0;

        const nextAddersInitial = [...activeAdders];
        nextAddersInitial[index].quantity = currentQty;
        setActiveAdders(nextAddersInitial);

        const adder = nextAddersInitial[index];
        // Wait 10ms as per Angular's setTimeout
        setTimeout(async () => {
            updateLoaderCount(true);
            const scratchRow = activeAdders.length + 4;
            const payload = {
                ExcelFileName: "Roofing Calculator", 
                SheetName: "Adder Pricing",
                InputValues: [
                    { Type: 'string', CellNumber: `F${scratchRow}`, CellValue: adder.item },
                    { Type: 'string', CellNumber: `G${scratchRow}`, CellValue: String(currentQty) }
                ],
                OutputCells: [`I${scratchRow}`, `J${scratchRow}`, `K${scratchRow}`, `G${scratchRow}`, "C7", "I24"]
            };

            try {
                const res = await masterServices.cascadingDropDown(payload);
                const output = Array.isArray(res) ? res : (res as any)?.OutputValues || [];
                
                const unitPrice = Number(getValue(`I${scratchRow}`, output) || 0);
                const subTotal = Number(getValue(`J${scratchRow}`, output) || 0);
                const isComm = getValue(`K${scratchRow}`, output) === 'Y';
                const skyDiscount = Number(getValue('I24', output) || 0);

                let skyLightAlreadyAdded = false;
                const nextAddersResults = activeAdders.map((a, i) => {
                    if (a.item.includes('Skylight') && i !== index) {
                        skyLightAlreadyAdded = true;
                    }
                    
                    if (i === index) {
                        const updated = {
                            ...a,
                            quantity: currentQty,
                            unitPrice,
                            subTotal,
                            commissionable: isComm
                        };
                        
                        if (updated.item.includes('Skylight') && !skyLightAlreadyAdded) {
                            updated.discount = skyDiscount || 0;
                        } else if (updated.item.includes('Skylight')) {
                            updated.discount = 0;
                        }
                        return updated;
                    }
                    return a;
                });

                setActiveAdders(nextAddersResults);
                await calculateGlobalAdders(nextAddersResults);
            } catch (err) {
                console.error('Failed to update adder quantity', err);
            } finally {
                updateLoaderCount(false);
            }
        }, 10);
    };

    const handleOtherManualCalculation = () => {
        const isRRR = opportunityDetails?.OpportunityInterest?.RRR;
        let runningTotal = 0;
        
        const nextAdders = activeAdders.map(a => {
            if (a.item === 'Other') {
                const newSub = Number(a.quantity || 0) * Number(a.unitPrice || 0);
                runningTotal += newSub;
                return { ...a, subTotal: newSub };
            }
            runningTotal += Number(a.subTotal || 0);
            return a;
        });

        let finalCost = runningTotal;
        if (totalCommissionAmount !== 0) {
            finalCost += Number(totalCommissionAmount);
        }
        if (isRRR) {
            finalCost += Number(rrPrice);
        }

        setActiveAdders(nextAdders);
        setSummaryStats(prev => ({ ...prev, totalAdderCost: finalCost }));
    };

    const removeAdder = (index: number) => {
        let discount = 0;
        const target = activeAdders[index];
        
        if (target?.item.includes('Skylight')) {
            discount = target.discount || 0;
        }

        let filtered = activeAdders.filter((_, i) => i !== index);
        
        const nextAdders = filtered.map((a, i) => {
            let updated = { ...a };
            if (updated.item.includes('Skylight') && discount) {
                updated.discount = discount;
                discount = 0;
            }
            return updated;
        });

        setActiveAdders(nextAdders);
        calculateGlobalAdders(nextAdders);
    };

    const formatCurrency = (val: any) => {
        const num = Number(val || 0);
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    if (!confirmQuoteCellKey) return null;

    const showTotalRoofingOverride = pricingOverrideType === 'Fixed';
    const showSlopePriceOverrides = pricingOverrideType === 'RoofSquares' || (pricingOverrideType === 'Fixed' && (selectedFinanceMethod === 'Cash Contract' || selectedFinanceMethod?.toLowerCase().includes('loan')));

    const totalRoofingOverrideError = showTotalRoofingOverride && Number(totalRoofingOverride || 0) === 0;
    const normalSlopePriceOverrideError = !totalRoofingOverrideError && showSlopePriceOverrides && Number(normalSlopePriceOverride || 0) === 0;
    const lowSlopePriceOverrideError = !totalRoofingOverrideError && !normalSlopePriceOverrideError && showSlopePriceOverrides && pricingOverrideType === 'RoofSquares' && Number(lowSlopePriceOverride || 0) === 0;
    const invalidDepositAmount = isCashPartner && deposit > (totalSales || 0);

    const roofHeaderStats: GqSectionStat[] = [
        { label: 'Total adder amount', value: `$${formatCurrency(summaryStats.totalAdderCost)}` },
        { label: 'Total roof amount', value: `$${formatCurrency(summaryStats.totalRoofCost)}` },
        { label: 'Total sales amount', value: `$${formatCurrency(totalSales)}`, emphasize: true },
    ];
    if (selectedFinanceMethod?.toLowerCase().includes('loan')) {
        roofHeaderStats.push({
            label: 'Financed amount',
            value: `$${formatCurrency(summaryStats.financedAmount)}`,
        });
    }
    roofHeaderStats.push({
        label: 'Commission amount',
        value: `$${formatCurrency(totalCommissionAmount)}`,
    });

    return (
        <div className="obd-roof-quote-details__container obd-roof-quote-details__container--split obd-gq-section">
            <GenerateQuoteSectionHead
                title="Roof"
                stats={roofHeaderStats}
                ariaLabel="Roof quote totals"
            />
            <div className="obd-gq-section-body">
            <div className="obd-generate-quote-split obd-generate-quote-split--no-aside">
                <div className="obd-generate-quote-split__forms">
                <div className="obd-roof-quote-details__top-grid">
                    <div className="obd-roof-quote-details__field-group">
                        <label className="obd-roof-quote-details__label">Roof squares (total)</label>
                        <Input className="obd-roof-quote-details__input" value={roofMetrics.total_roof_squares} disabled />
                    </div>
                    {/* <div className="obd-roof-quote-details__field-group">
                        <label className="obd-roof-quote-details__label">Grid Method</label>
                        <OBDSelect 
                            className="obd-roof-quote-details__select" 
                            value={confirmQuoteCellKey?.methord || 'Loan'} 
                            disabled 
                            options={[{label: confirmQuoteCellKey?.methord || 'Loan', value: confirmQuoteCellKey?.methord || 'Loan'}]}
                            popupClassName={selectDropdownClassName}
                        />
                    </div> */}
                    <div className="obd-roof-quote-details__field-group">
                        <label className="obd-roof-quote-details__label">Finance Method</label>
                        <OBDSelect 
                            className="obd-roof-quote-details__select" 
                            value={selectedFinanceMethod || 'Loan'} 
                            onChange={(val) => setSelectedFinanceMethod(val)} 
                            options={financeMethods} 
                            disabled
                            popupClassName={selectDropdownClassName}
                        />
                    </div>
                    {!isCashPartner && selectedFinanceMethod?.toLowerCase().includes('loan') && (
                        <div className="obd-roof-quote-details__field-group">
                            <label className="obd-roof-quote-details__label">Term & APR</label>
                            <OBDSelect 
                                className="obd-roof-quote-details__select" 
                                value={termApr} 
                                onChange={(val) => setTermApr(val)} 
                                options={termOptions} 
                                disabled={Boolean(solarMasterSync?.enabled)}
                                popupClassName={selectDropdownClassName}
                            />
                        </div>
                    )}
                    <div className="obd-roof-quote-details__field-group">
                        <label className="obd-roof-quote-details__label">Deposit Amount</label>
                        <CurrencyInput
                            className={`obd-roof-quote-details__input ${invalidDepositAmount ? 'ant-input-status-error' : ''}`}
                            value={Number(deposit).toFixed(2)}
                            onValueChange={(val) => setDeposit(val ?? 0)}
                            onBlur={handlePricingBlur}
                            prefix="$"
                            decimalsLimit={2}
                            fixedDecimalLength={2}
                            allowNegativeValue={false}
                        />
                        {invalidDepositAmount && <span className="obd-roof-quote-details__error-msg">The entered Deposit Amount cannot be greater than ${formatCurrency(totalSales)}</span>}
                    </div>
                    {isCashPartner && (
                        <div className="obd-roof-quote-details__field-group">
                            <label className="obd-roof-quote-details__label">Plus adders upon completion of work</label>
                            <CurrencyInput
                                className="obd-roof-quote-details__input"
                                value={Number(summaryStats.plusAddersUponCompletion).toFixed(2)}
                                onValueChange={(val) => setSummaryStats(prev => ({ ...prev, plusAddersUponCompletion: val ? Number(val) : 0 }))}
                                prefix="$"
                                decimalsLimit={2}
                                fixedDecimalLength={2}
                                allowNegativeValue={false}
                            />
                        </div>
                    )}
                    <div className="obd-roof-quote-details__field-group">
                        <label className="obd-roof-quote-details__label">Pricing Override Type</label>
                        <OBDSelect 
                            className="obd-roof-quote-details__select" 
                            value={pricingOverrideType} 
                            onChange={(val) => {
                                setPricingOverrideType(val);
                                setTotalRoofingOverride(0);
                                if (val === 'RoofSquares') {
                                    const defaultNormal = pq.PricePerSqFeet ? Number(Number(pq.PricePerSqFeet).toFixed(2)) : '';
                                    const defaultLow = defaultNormal ? Number((Number(defaultNormal) + 0.7).toFixed(2)) : '';
                                    setNormalSlopePriceOverride(defaultNormal);
                                    setLowSlopePriceOverride(defaultLow);
                                    setCommittedPricing(prev => ({ 
                                        ...prev, 
                                        totalOverride: 0,
                                        normalPrice: Number(defaultNormal) || 0,
                                        lowPrice: Number(defaultLow) || 0
                                    }));
                                } else {
                                    // Fixed - clear slopes
                                    setNormalSlopePriceOverride('');
                                    setLowSlopePriceOverride('');
                                    setCommittedPricing(prev => ({ 
                                        ...prev, 
                                        totalOverride: 0,
                                        normalPrice: 0, 
                                        lowPrice: 0 
                                    }));
                                }
                            }} 
                            options={[
                                { label: 'Roof Squares', value: 'RoofSquares' },
                                { label: 'Fixed', value: 'Fixed' }
                            ]} 
                            popupClassName={selectDropdownClassName}
                        />
                    </div>

                    {showTotalRoofingOverride && (
                        <div className="obd-roof-quote-details__field-group">
                            <label className="obd-roof-quote-details__label">Total Roofing Override</label>
                            <CurrencyInput
                                className={`obd-roof-quote-details__input ${totalRoofingOverrideError ? 'ant-input-status-error' : ''}`}
                                value={Number(totalRoofingOverride).toFixed(2)}
                                onValueChange={(val) => setTotalRoofingOverride(val ?? '')}
                                onBlur={handlePricingBlur}
                                prefix="$"                            
                                decimalsLimit={2}
                                fixedDecimalLength={2}
                                allowNegativeValue={false}
                            />
                            {totalRoofingOverrideError && <span className="obd-roof-quote-details__error-msg">Total Roofing Override is required.</span>}
                        </div>
                    )}

                    {showSlopePriceOverrides && (
                        <>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">Normal slope override ({roofMetrics.normal_slope_squares} sq's)</label>
                                <CurrencyInput
                                    className={`obd-roof-quote-details__input ${normalSlopePriceOverrideError ? 'ant-input-status-error' : ''}`}
                                    value={Number(normalSlopePriceOverride).toFixed(2)}
                                    onValueChange={(val) => handleNormalPriceChange(val ?? '')}
                                    onBlur={handlePricingBlur}
                                    prefix="$"
                                    disabled={pricingOverrideType === 'Fixed'}                                 
                                    decimalsLimit={2}
                                    fixedDecimalLength={2}
                                    allowNegativeValue={false}
                                />
                                {normalSlopePriceOverrideError && <span className="obd-roof-quote-details__error-msg">Normal Slope Price Override is required.</span>}
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">Low slope override ({roofMetrics.low_slope_squares} sq's)</label>
                                <CurrencyInput
                                    className={`obd-roof-quote-details__input ${lowSlopePriceOverrideError ? 'ant-input-status-error' : ''}`}
                                    value={Number(lowSlopePriceOverride).toFixed(2)}
                                    onValueChange={(val) => setLowSlopePriceOverride(val ?? '')}
                                    onBlur={handlePricingBlur}
                                    prefix="$"
                                    disabled={true}
                                    decimalsLimit={2}
                                    fixedDecimalLength={2}
                                    allowNegativeValue={false}
                                />
                                {lowSlopePriceOverrideError && <span className="obd-roof-quote-details__error-msg">Low Slope Price Override is required.</span>}
                            </div>
                        </>
                    )}
                </div>

                <div className="obd-roof-quote-details__adders-section">
                    <h3 className="obd-roof-quote-details__adders-title">ROOF ADDERS</h3>
                    <div className="obd-roof-quote-details__adder-controls-label">Choose Roof Adder</div>
                    <div className="obd-roof-quote-details__adder-row">
                        <OBDSelect
                            className="obd-roof-quote-details__select"
                            style={{ flex: 1 }}
                            value={selectedAdderType}
                            onChange={(val) => setSelectedAdderType(val)}
                            options={adderOptions}
                            placeholder="Select adder type"
                            popupClassName={selectDropdownClassName}
                        />
                        <button className="obd-roof-quote-details__plus-btn" onClick={handleAddAdder}>
                            <Plus size={20} weight="bold" />
                        </button>
                    </div>

                    <div className="obd-roof-quote-details__adder-cards">
                        {opportunityDetails?.OpportunityInterest?.RRR && (
                            <div className="obd-roof-quote-details__adder-card obd-roof-quote-details__adder-card--rrr">
                                <div className="obd-roof-quote-details__adder-card-header">
                                    <span className="obd-roof-quote-details__adder-card-name">R&R</span>
                                    {rrPriceLoading && <Spin size="small" />}
                                </div>
                                <div className="obd-roof-quote-details__adder-card-field" style={{ marginBottom: '16px' }}>
                                    <label className="obd-roof-quote-details__adder-card-label">Description</label>
                                    <Input className="obd-roof-quote-details__input--readonly" value="R&R Cost" disabled />
                                </div>
                                <div className="obd-roof-quote-details__adder-card-footer">
                                    <div className="obd-roof-quote-details__adder-card-field">
                                        <label className="obd-roof-quote-details__adder-card-label">Quantity (W)</label>
                                        <Input 
                                            className="obd-roof-quote-details__input--readonly" 
                                            value={(opportunityDetails?.RR_System_Size * 1000).toFixed(2)} 
                                            disabled 
                                        />
                                    </div>
                                    <div className="obd-roof-quote-details__adder-card-field">
                                        <label className="obd-roof-quote-details__adder-card-label">Unit Price</label>
                                        <CurrencyInput
                                            className={`obd-roof-quote-details__input ${Number(rrUnitPrice) < 0.65 ? 'ant-input-status-error' : ''}`}
                                            value={Number(rrUnitPrice).toFixed(2)}
                                            onValueChange={(val) => setRrUnitPrice(val ?? '')}
                                            onBlur={() => getRRPrice(true)}
                                            prefix="$"
                                            decimalsLimit={2}
                                            fixedDecimalLength={2}
                                            allowNegativeValue={false}
                                        />
                                    </div>
                                    <div className="obd-roof-quote-details__adder-card-field">
                                        <label className="obd-roof-quote-details__adder-card-label">Subtotal</label>
                                        <span className="obd-roof-quote-details__adder-card-value">${formatCurrency(rrPrice)}</span>
                                    </div>
                                </div>
                                {rrUnitPrice < 0.65 && (
                                    <div className="obd-roof-quote-details__error-msg" style={{ marginTop: '12px' }}>
                                        Below minimum approved pricing of 65 cents per watt, commission payment will be reduced.
                                    </div>
                                )}
                            </div>
                        )}

                        {activeAdders.map((adder, idx) => (
                            <div key={adder.id} className="obd-roof-quote-details__adder-card">
                                <div className="obd-roof-quote-details__adder-card-header">
                                    <span className="obd-roof-quote-details__adder-card-name">{adder.item}</span>
                                    <button className="obd-roof-quote-details__trash-btn" onClick={() => removeAdder(idx)}>
                                        <Trash size={18} />
                                    </button>
                                </div>
                                <div className="obd-roof-quote-details__adder-card-field" style={{ marginBottom: '16px' }}>
                                    <label className="obd-roof-quote-details__adder-card-label">Description</label>
                                    <Input 
                                        className="obd-roof-quote-details__input"
                                        value={adder.description}
                                        onChange={e => {
                                            const next = [...activeAdders];
                                            next[idx].description = e.target.value;
                                            setActiveAdders(next);
                                        }}
                                        placeholder="Description"
                                    />
                                </div>
                                <div className="obd-roof-quote-details__adder-card-footer">
                                    <div className="obd-roof-quote-details__adder-card-field">
                                        <label className="obd-roof-quote-details__adder-card-label">Quantity</label>
                                        <Input 
                                            type="number"
                                            className="obd-roof-quote-details__input"
                                            value={adder.quantity}
                                            onChange={e => {
                                                const next = [...activeAdders];
                                                next[idx].quantity = Number(e.target.value);
                                                setActiveAdders(next);
                                            }}
                                            onFocus={() => setIsInputFocused(true)}
                                            onBlur={e => updateAdderQuantity(idx, Number(e.target.value), !e.target.value || Number(e.target.value) < 0)}
                                        />
                                    </div>
                                    <div className="obd-roof-quote-details__adder-card-field">
                                        <label className="obd-roof-quote-details__adder-card-label">Unit Price</label>
                                        {adder.item === 'Other' ? (
                                            <CurrencyInput
                                                className="obd-roof-quote-details__input"
                                                value={Number(adder.unitPrice).toFixed(2)}
                                                onValueChange={(val) => {
                                                    const next = [...activeAdders];
                                                    next[idx].unitPrice = val ?? '';
                                                    next[idx].subTotal = (val ? Number(val) : 0) * (next[idx].quantity || 0);
                                                    setActiveAdders(next);
                                                }}
                                                onFocus={() => setIsInputFocused(true)}
                                                onBlur={handleOtherManualCalculation}
                                                prefix="$"
                                                decimalsLimit={2}
                                                fixedDecimalLength={2}
                                                allowNegativeValue={false}
                                            />
                                        ) : (
                                            <span className="obd-roof-quote-details__adder-card-value">${formatCurrency(adder.unitPrice)}</span>
                                        )}
                                    </div>
                                    <div className="obd-roof-quote-details__adder-card-field">
                                        <label className="obd-roof-quote-details__adder-card-label">Subtotal</label>
                                        <span className="obd-roof-quote-details__adder-card-value obd-roof-quote-details__adder-card-value--highlight">
                                            ${formatCurrency(adder.subTotal)}
                                        </span>
                                    </div>
                                </div>
                                {(adder.item.includes('Skylight') || (adder.item === 'Other' && adder.discount)) && adder.discount > 0 && (
                                    <div className="obd-roof-quote-details__adder-card-info" style={{ marginTop: '12px' }}>
                                        Skylight Credit with a quantity of 1 and unit price/subtotal of ${formatCurrency(adder.discount)}
                                    </div>
                                )}
                                {adder.item === 'Skylight Curb Kits' && (
                                    <div className="obd-roof-quote-details__adder-card-info">
                                        Skylight Curb Kits: “(Up to {RoofDiscountCount.Skylight_Curb_Kits} included in price)”
                                    </div>
                                )}
                                {adder.item === 'Plywood' && (
                                    <div className="obd-roof-quote-details__adder-card-info">
                                        Plywood: “(Up to {RoofDiscountCount.Plywood} included in price)”
                                    </div>
                                )}
                            </div>
                        ))}
                        {activeAdders.length === 0 && !opportunityDetails?.OpportunityInterest?.RRR && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>No adders added yet.</div>
                        )}
                    </div>
                </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default RoofQuoteDetails;
